import type { TraceQuestion, TraceAllocationCapability, TraceInsights } from "@authe/fe-commons/flows";
import type { InteractionGraphDTO } from "@authe/fe-commons/models";
import type { traceFiltersToWire } from "@/api/tracersSource";

export type TraceConfig = ReturnType<typeof traceFiltersToWire> & {
  question?: TraceQuestion;
  read_limit?: number;
  seeds: string[];
  direction: "outbound" | "inbound" | "both";
  max_hops: number;
  alarms: string[];
  listed: string[];
};
export type LocalTransaction = {
  id: string;
  tx_hash: string;
  chain_id: string;
  block_number: number;
  timestamp: number;
  label: string;
  finality?: string;
  from_address?: string;
  to_address?: string;
};
export type Firing = {
  alarm: string;
  tx_hash: string;
  chain: string;
  finality?: string;
  label: string;
};
/** One thing the platform's situation engine raised over the chains' recorded runs. */
export type LocalSituation = {
  id: string | null;
  finding_id?: string | null;
  signal: string | null;
  transactions: { id: string | null; tx_hash: string; chain_id: string | null; label: string }[];
  subjects: { address: string; chain_id?: string | null; role: string; accused?: boolean; basis?: string }[];
};
export type Snapshot = {
  session_id: string;
  revision: number;
  state: "starting" | "updating" | "ready" | "error";
  reader_progress?: { phase: "reading" | "normalizing" | "decoding" | "ready"; processed: number; total: number } | null;
  error?: string;
  issue?: { code: "networks_stopped"; networks: string[] };
  updated_at?: number;
  config: TraceConfig;
  limits?: { max_hops: number; max_read_limit?: number; allocation_methods?: TraceAllocationCapability[] };
  networks: { name: string; chain_id: string; kind: string; rpc_url: string }[];
  /** Registry rows for chains only the recorded runs know (a protocol network's Ethereum, a NEAR localnet). */
  chains?: Record<string, unknown>[];
  heads?: Record<string, number>;
  transaction_count?: number;
  transaction_refs?: Record<string, string>;
  transactions?: LocalTransaction[];
  situations?: { state: "idle" | "reading" | "ready" | "error"; raised: LocalSituation[]; errors?: string[] };
  labels?: Record<string, string>;
  graph?: InteractionGraphDTO | null;
  token_metadata?: { address: string; chainId: string; decimals: number }[];
  /** Scenario references resolved on the selected local ledger, for shared forms. */
  token_addresses?: Record<string, string>;
  trace?: {
    status: string;
    status_reason?: string;
    walks: number;
    resumable?: boolean;
    frontier_count?: number;
    hops_walked?: number;
    continuation_hops_max?: number | null;
    continuation_reason?: string | null;
    insights?: TraceInsights;
    bounded_by?: string[];
    receipts?: Record<string, unknown>[];
    finality?: unknown;
    coverage_gaps?: Record<string, unknown>[];
    lookahead?: { edges?: Record<string, unknown>[] };
  } | null;
  waiting?: string[];
  warnings?: string[];
  armed?: { name: string; chain: string; address: string }[];
  alarms?: {
    firings: Firing[];
    expected: { alarm: string; finality: string }[];
    errors: string[];
  };
};

/** A pairing code can only be sent to a literal loopback origin, never a URL supplied by a site. */
export function agentOrigin(input: string): string {
  const url = new URL(input);
  if (
    url.protocol !== "http:" ||
    !["127.0.0.1", "localhost"].includes(url.hostname) ||
    !url.port ||
    url.username ||
    url.password ||
    url.pathname !== "/" ||
    url.search ||
    url.hash
  ) {
    throw new Error(
      "Use the agent's loopback URL, for example http://127.0.0.1:8788"
    );
  }
  return url.origin;
}

export class LocalAgent {
  readonly origin: string;
  constructor(origin: string, private readonly code: string) {
    this.origin = agentOrigin(origin);
    if (!code.trim())
      throw new Error("Enter the pairing code printed by bc dashboard");
  }
  async request<T>(
    path: string,
    signal?: AbortSignal,
    config?: TraceConfig
  ): Promise<T> {
    const response = await fetch(`${this.origin}/v1/${path}`, {
      method: config ? "PUT" : "GET",
      signal,
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      headers: {
        Authorization: `Bearer ${this.code}`,
        ...(config ? { "Content-Type": "application/json" } : {}),
      },
      ...(config ? { body: JSON.stringify(config) } : {}),
    });
    const value = await response.json();
    if (!response.ok)
      throw new Error(value.error ?? `Local agent returned ${response.status}`);
    return value as T;
  }
}

export const entries = (value: string): string[] =>
  value
    .split(/[,\n]/)
    .map((v) => v.trim())
    .filter(Boolean);
